import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import { toast } from 'sonner';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { useGetThemeById } from '../../domain/hooks/use_theme';
import ThemeConstants from '../../../../config/theme/theme_constants';
import { UserInterface } from '../../../../core/components/user/domain/interfaces/user_interface';
import { CareerStatusEnum } from '../../../../core/enum/career_status_enum';
import { useDeleteUserThemeInProgress } from '../../domain/hooks/use_user_theme';
import ArrowBackIcon from '../../../../assets/images/arrow_back_black_icon.svg?react';
import ReactMarkdown from 'react-markdown';
import { markdownComponents } from '../components/markdown_components';
import UvibesIcon from '../../../../assets/images/uvibes_icon.svg?react';
import { SyncLoader } from 'react-spinners';
import { getUserThemeByThemeId } from '../../domain/utils/get_current_user_theme';
import { useTranslation } from '../../../../i18n/useTranslation';

const ThemeDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { themeId } = useParams();
  const theme = ThemeConstants();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCurrent = (location.state as { fromCurrent?: boolean } | null)?.fromCurrent === true;
  const { careerStatus, company, course_theme } = useOutletContext<UserInterface>();
  const userTheme = useMemo(() => getUserThemeByThemeId(course_theme, themeId, company?.id), [course_theme, themeId, company?.id]);
  const { data: themeData, error: themeError, refetch } = useGetThemeById(themeId);
  const { mutate: deleteUserThemeInProgress, isPending: isDeletingUserTheme } = useDeleteUserThemeInProgress();
  const [confirmAbandonOpen, setConfirmAbandonOpen] = useState(false);

  useEffect(() => {
    if (themeError) {
      const data = themeError.response?.data as { message?: string } | undefined;
      const errorMessage = data?.message || 'An error occurred';
      if (errorMessage === "id contains invalid characters" || errorMessage === "Theme not found") {
        toast.error(t('course.theme_not_found'));
        navigate('/career', { replace: true });
      }
    }
  }, [themeError, navigate, t]);

  const handleStart = useCallback(() => {
    const extra = company?.permissions.course === true ? '/quizz' : '/resources';
    navigate(`/career/${themeId}${extra}`);
  }, [navigate, themeId, company?.permissions.course]);

  const openAbandonConfirm = useCallback(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    setConfirmAbandonOpen(true);
  }, []);

  const closeAbandonConfirm = useCallback(() => {
    if (isDeletingUserTheme === false) {
      setConfirmAbandonOpen(false);
    }
  }, [isDeletingUserTheme]);

  const handleAbandonTheme = useCallback(() => {
    if (isDeletingUserTheme === false) {
      deleteUserThemeInProgress(undefined, {
        onSuccess: () => {
          navigate('/career/start', { replace: true });
        },
      });
    }
  }, [deleteUserThemeInProgress, navigate, isDeletingUserTheme]);

  const handleViewCertificate = useCallback(() => {
    navigate(`/career/${themeId}/certificate`);
  }, [navigate, themeId]);

  useEffect(() => {
    if (company?.permissions.course === true && careerStatus === CareerStatusEnum.Start) {
      navigate('/career/start', { replace: true });
    }
  }, [careerStatus, company?.permissions.course, navigate]);

  return (
    <>
    <div className="min-h-dvh py-[5.6dvh] max-lg:px-[3%] lg:px-[1%] flex flex-col gap-9 items-center relative">
      <UvibesIcon className='fixed top-1/2 left-1/2 translate-y-[-54%] -translate-x-1/2 w-160 h-auto -z-30' color={`${themeData?.color ? themeData.color : theme.UvibesPink}10`}/>
      <div className='flex flex-col gap-3 w-full items-center'>
        <button className='cursor-pointer self-start w-6 h-6 flex items-center justify-center' onClick={() => navigate(-1)}>
          <ArrowBackIcon color='black' className='w-4.25 h-auto'/>
        </button>

        <div
          className="py-2 px-11 rounded-[15px] min-h-20 flex items-center justify-center"
          style={{ backgroundColor: themeData?.color ?? theme.UvibesPink }}
        >
          {themeData || themeError ? <h1 className="text-white font-bold text-[2rem] text-center leading-none line-clamp-2">{themeError ? t('common.error.title') : themeData!.name}</h1> : <SyncLoader speedMultiplier={0.5} size={10} color="white" className='px-12'/>}
        </div>
      </div>

      {themeError
        ? <div className="flex flex-col flex-1 justify-center gap-4 items-center">
            <span className="text-sm text-medium text-center">{t('common.error.occurred')}</span>
            <button
                className="px-4 py-2 rounded-[18px] text-white font-semibold text-sm cursor-pointer"
                style={{ background: theme.UvibesPink }}
                onClick={() => refetch()}
            >
                {t('common.button.retry')}
            </button>
        </div>
        : <div className="prose prose-lg max-w-153 flex-1 flex flex-col">
          <ReactMarkdown
              components={markdownComponents}
          >
              {themeData?.description}
          </ReactMarkdown>
        </div>}

      {themeData 
        ? company?.permissions.course === true && userTheme
          ? (
            <div className="flex flex-col gap-4">
              <button
                className="text-white px-6 py-2 cursor-pointer rounded-full z-1000"
                style={{ backgroundColor: themeData?.color ?? theme.UvibesPink }}
                onClick={handleStart}
              >
                {t('course.detail.reread_evaluation')}
              </button>
              {userTheme.status === 'in_progress'
                ? <button
                  className="text-white px-6 py-2 cursor-pointer rounded-full z-1000 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: themeData?.color ?? theme.UvibesPink }}
                  onClick={openAbandonConfirm}
                  disabled={isDeletingUserTheme}
                >
                  {isDeletingUserTheme ? <SyncLoader speedMultiplier={0.5} size={6} color="white" /> : t('course.abandon.button')}
                </button>
                : <button
                  className="text-white px-6 py-2 cursor-pointer rounded-full z-1000"
                  style={{ backgroundColor: themeData?.color ?? theme.UvibesPink }}
                  onClick={handleViewCertificate}
                >
                  {t('course.detail.view_certificate')}
                </button>
              }
            </div>
          )
         : company?.permissions.course !== true && fromCurrent ? <div/> : (
          <button
            className="text-white px-6 py-2 cursor-pointer rounded-full z-1000"
            style={{ backgroundColor: themeData?.color ?? theme.UvibesPink }}
            onClick={handleStart}
          >
            {company?.permissions.course === true ? t('course.detail.work_skill') : t('course.detail.explore_theme')}
          </button>
        ) : <div/>}
    </div>

    <Dialog
        fullWidth={true}
        onClose={closeAbandonConfirm}
        open={confirmAbandonOpen}
        slotProps={{
            backdrop: {
                style: {
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                },
            },
            container: {
                className: '!w-screen'
            },
            paper: {
                className: '!rounded-[20px] !m-3 !w-full',
            },
        }}
    >
        <div className='flex flex-col items-center gap-7 w-full bg-white px-9 pt-15.5 pb-11.5'>
            <div className="flex flex-col items-center gap-6 text-black">
                <h2 className='text-xl font-medium text-center'>{t('course.abandon.title')}</h2>
                <p className='text-xs text-center'>{t('course.abandon.description')}</p>
            </div>
            <div className='flex flex-col items-center gap-3.75 w-full'>
                <button
                    className={`rounded-full text-white font-bold px-8 py-2.25 w-full ${isDeletingUserTheme ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    style={{ backgroundColor: themeData?.color ?? theme.UvibesPink }}
                    onClick={handleAbandonTheme}
                    disabled={isDeletingUserTheme}
                >
                    {isDeletingUserTheme ? t('course.abandon.pending') : t('course.abandon.button')}
                </button>
                <button
                    className={`rounded-full font-bold px-8 py-2.25 w-full ${isDeletingUserTheme ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    style={{
                        backgroundColor: `${themeData?.color ?? theme.UvibesPink}10`,
                        color: themeData?.color ?? theme.UvibesPink,
                        boxShadow: `inset 0 0 0 1px ${themeData?.color ?? theme.UvibesPink}`
                    }}
                    onClick={closeAbandonConfirm}
                    disabled={isDeletingUserTheme}
                >
                    {t('common.button.cancel')}
                </button>
            </div>
        </div>
    </Dialog>
    </>
  );
};

export default ThemeDetailPage;
